import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { Loader2 } from "lucide-react";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminLogin from "@/pages/AdminLogin";
import CustomerLayout from "@/components/CustomerLayout";
import AdminLayout from "@/components/AdminLayout";
import CustomerDashboard from "@/pages/CustomerDashboard";
import QRScanner from "@/pages/QRScanner";
import PointsLedger from "@/pages/PointsLedger";
import ProductCatalog from "@/pages/ProductCatalog";
import ProductDetail from "@/pages/ProductDetail";
import ProfilePage from "@/pages/ProfilePage";
import AboutPage from "@/pages/AboutPage";
import EnquiryPage from "@/pages/EnquiryPage";
import ComplaintPage from "@/pages/ComplaintPage";
import ReferralPage from "@/pages/ReferralPage";
import VideosPage from "@/pages/VideosPage";
import ProductDemosPage from "@/pages/ProductDemosPage";
import LabInfoPage from "@/pages/LabInfoPage";
import RedeemPage from "@/pages/RedeemPage";
import DistributorLocator from "@/pages/DistributorLocator";
import InsightsPage from "@/pages/InsightsPage";
import WebViewPage from "@/pages/WebViewPage";
import AdminBanners from "@/pages/AdminBanners";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminProducts from "@/pages/AdminProducts";
import AdminReagentMapping from "@/pages/AdminReagentMapping";
import AdminQRCodes from "@/pages/AdminQRCodes";
import AdminRejectedQRs from "@/pages/AdminRejectedQRs";
import AdminERPDiagnostics from "@/pages/AdminERPDiagnostics";
import AdminConfig from "@/pages/AdminConfig";
import AdminReports from "@/pages/AdminReports";
import AdminContentManager from "@/pages/AdminContentManager";
import AdminRegistrations from "@/pages/AdminRegistrations";
import AdminEnquiries from "@/pages/AdminEnquiries";
import AdminComplaints from "@/pages/AdminComplaints";
import AdminRedemptions from "@/pages/AdminRedemptions";
import AdminSplashScreens from "@/pages/AdminSplashScreens";
import AdminAssets from "@/pages/AdminAssets";
import NotificationsPage from "@/pages/NotificationsPage";
import MyInstrumentsPage from "@/pages/MyInstrumentsPage";
import AdminDistributors from "@/pages/AdminDistributors";
import AdminQRGenerator from "@/pages/AdminQRGenerator";
import AdminInstruments from "@/pages/AdminInstruments";
import AdminAdminUsers from "@/pages/AdminAdminUsers";
import SplashScreen from "@/components/SplashScreen";
import OfflineBanner from "@/components/OfflineBanner";
import AdminApiLogs from "@/pages/AdminApiLogs";
import AdminScanApprovals from "@/pages/AdminScanApprovals";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FA]">
      <div className="text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" />
      </div>
    </div>
  );
}

function CustomerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  // KYC gate: user must be admin-approved to access the app.
  // Legacy users (no field) are treated as approved for backward compat.
  const status = user.registration_status;
  if (status && status !== "approved") {
    return <Navigate to="/login?pending=1" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function PublicRoute({ children, showSplash }) {
  const { user, loading } = useAuth();
  const [splashDone, setSplashDone] = useState(false);
  if (loading) return <LoadingScreen />;
  // Only redirect to /app if user is approved (or legacy with no status field).
  // Pending/submitted/rejected users stay on login so they see the gate message.
  if (user && user !== false) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    const s = user.registration_status;
    if (!s || s === "approved") return <Navigate to="/app" replace />;
  }
  if (showSplash && !splashDone) {
    return <SplashScreen onComplete={() => setSplashDone(true)} />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute showSplash><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/admin/login" element={<PublicRoute><AdminLogin /></PublicRoute>} />

      <Route path="/app" element={<CustomerRoute><CustomerLayout /></CustomerRoute>}>
        <Route index element={<CustomerDashboard />} />
        <Route path="scan" element={<QRScanner />} />
        <Route path="points" element={<PointsLedger />} />
        <Route path="products" element={<ProductCatalog />} />
        <Route path="products/:code" element={<ProductDetail />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="enquiry" element={<EnquiryPage />} />
        <Route path="complaints" element={<ComplaintPage />} />
        <Route path="referral" element={<ReferralPage />} />
        <Route path="product-demos" element={<ProductDemosPage />} />
        <Route path="lab-info" element={<LabInfoPage />} />
        <Route path="redeem" element={<RedeemPage />} />
        <Route path="distributors" element={<DistributorLocator />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="web" element={<WebViewPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="my-instruments" element={<MyInstrumentsPage />} />
        <Route path="videos" element={<VideosPage />} />
      </Route>

      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="banners" element={<AdminBanners />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="registrations" element={<AdminRegistrations />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="reagent-mapping" element={<AdminReagentMapping />} />
        <Route path="instruments" element={<AdminInstruments />} />
        <Route path="qr-codes" element={<AdminQRCodes />} />
        <Route path="rejected-qrs" element={<AdminRejectedQRs />} />
        <Route path="scan-approvals" element={<AdminScanApprovals />} />
        <Route path="erp-diagnostics" element={<AdminERPDiagnostics />} />
        <Route path="content/:type" element={<AdminContentManager />} />
        <Route path="enquiries" element={<AdminEnquiries />} />
        <Route path="complaints" element={<AdminComplaints />} />
        <Route path="redemptions" element={<AdminRedemptions />} />
        <Route path="splash-screens" element={<AdminSplashScreens />} />
        <Route path="assets" element={<AdminAssets />} />
        <Route path="distributors" element={<AdminDistributors />} />
        <Route path="qr-generator" element={<AdminQRGenerator />} />
        <Route path="config" element={<AdminConfig />} />
        <Route path="admin-users" element={<AdminAdminUsers />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="api-logs" element={<AdminApiLogs />} />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
